import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import WithdrawApi from "../api/withdraw";
import WithdrawPrintApi from "../api/withdraw-print";
import AuthContext from "../components/auth-context";
import { localizeNumbers, getMoneyText } from "../functions";
import moment from "moment-jalaali";

interface Commission {
  AgentTitle: string;
  Amount: string;
  Description?: string;
}

interface ReceiptItem {
  ProductId: number | null;
  Count: number;
  Unit?: string;
}

interface Product {
  Identifier: number;
  Title: string;
  Unit?: string;
  Price: number;
}

interface Receipt {
  ReceiptItems: ReceiptItem[];
}

interface IWithdrawDetail {
  WithdrawType?: string;
  DetailLink?: string;
  Id?: number;
  RawAmount?: number;
  TaxAmount?: number;
  Discount?: number;
  TotalAmount?: number;
  Commissions: Commission[];
  DateTime?: string;
  Description?: string;
  Receipt: Receipt;
  Products: Product[];
  HasPrintWithdrawResource: Boolean;
}

function Withdraws() {
  let { id } = useParams();
  const context = useContext(AuthContext);
  const [model, setModel] = useState<IWithdrawDetail>({
    WithdrawType: undefined,
    DetailLink: undefined,
    Id: undefined,
    RawAmount: undefined,
    TaxAmount: undefined,
    Discount: undefined,
    TotalAmount: undefined,
    Commissions: [],
    DateTime: undefined,
    Description: undefined,
    Receipt: { ReceiptItems: [] },
    Products: [],
    HasPrintWithdrawResource: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getToman = (value: number) => {
    let money = getMoneyText(value);
    return `${money.amount} ${money.postFix}`;
  };

  useEffect(() => {
    new WithdrawApi(context)
      .get(Number(id))
      .then((data) => {
        const withdrawDetail: IWithdrawDetail = {
          Id: data.id,
          RawAmount: data.rawAmount,
          TaxAmount: data.taxAmount,
          Discount: data.discount,
          TotalAmount: data.totalAmount,
          Commissions: data.commissions || [],
          DateTime: data.dateTime,
          Description: data.description,
          HasPrintWithdrawResource: data.PrintWithdrawResource,
          Receipt: {
            ReceiptItems: data.receipt.map((item: any) => ({
              ProductId: item.productId,
              Count: 1,
              Unit: "عدد",
            })),
          },
          Products: data.products.map((p: Product) => ({
            Identifier: p.Identifier,
            Title: p.Title,
            Unit: p.Unit,
            Price: p.Price,
          })),
        };

        setModel(withdrawDetail);
      })
      .catch(() => setErrorMessage("Failed to fetch withdrawal details."));
    // eslint-disable-next-line
  }, [id]);

  const handlePrintInvoice = () => {
    setErrorMessage(null);
    setIsLoading(true);

    new WithdrawPrintApi(context)
      .post(Number(id))
      .then((fileResult) => {
        console.log("fileResult:", fileResult);

        if (fileResult && fileResult.fileContents) {
          const byteCharacters = atob(fileResult.fileContents);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: fileResult.contentType });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = fileResult.fileDownloadName || `${id}.docx`;
          link.click();
        } else {
          console.error("Invalid file format:", fileResult);
        }
      })
      .catch(() => setErrorMessage("Error while printing."))
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="p-6">
      <input type="hidden" id="MSCat" value="finance" />

      <table className="w-full border-collapse border border-gray-300 rounded-lg bg-white shadow-md">
        <tbody>
          {[
            { label: "مبلغ", value: model?.RawAmount },
            { label: "مالیات", value: model?.TaxAmount },
            { label: "تخفیف", value: model?.Discount },
            { label: "مبلغ کل", value: model?.TotalAmount },
          ].map((row, index) => (
            <tr key={index} className="border-b border-gray-200">
              <td className="px-6 py-4 text-right font-medium">{row.label}</td>
              <td className="px-6 py-4 text-left">
                {getToman(row.value!) || "-"}
              </td>
            </tr>
          ))}
          <tr>
            <td className="px-6 py-4 text-right font-medium">زمان</td>
            <td className="px-6 py-4 text-left">
              {localizeNumbers(
                moment(model.DateTime, "jYYYYjMMjDD").format("jYYYY/jMM/jDD")
              )}
            </td>
          </tr>
          <tr>
            <td className="px-6 py-4 text-right font-medium">توضیحات</td>
            <td className="px-6 py-4 text-left">
              {localizeNumbers(model?.Description!) || "-"}
            </td>
          </tr>
        </tbody>
      </table>

      {model?.Commissions?.length > 0 && (
        <table className="w-full mt-6 border-collapse border border-gray-300 bg-white shadow-md">
          <tbody>
            {model.Commissions.map((commission, index) => (
              <tr key={index} className="border-b border-gray-200">
                <td className="px-6 py-4 text-right font-medium">
                  کمیسیون {commission.AgentTitle}
                </td>
                <td className="px-6 py-4 text-left">{commission.Amount}</td>
                {commission.Description && (
                  <td className="px-6 py-4">{commission.Description}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {model?.Receipt?.ReceiptItems?.length > 0 && (
        <table className="w-full mt-6 border-collapse border border-gray-300 bg-white shadow-md">
          <thead className="bg-gray-200">
            <tr>
              {["مورد", "تعداد", "واحد", "قیمت", "مبلغ"].map(
                (header, index) => (
                  <th
                    key={index}
                    className="px-6 py-3 text-right border border-gray-300"
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {model.Receipt.ReceiptItems.map((item, index) => {
              const product = model.Products.find(
                (p) => p.Identifier === item.ProductId
              );
              return (
                <tr key={index} className="border-b border-gray-200">
                  <td className="px-6 py-4">{product?.Title || "نامشخص"}</td>
                  <td className="px-6 py-4">{item.Count}</td>
                  <td className="px-6 py-4">{product?.Unit || "عدد"}</td>
                  <td className="px-6 py-4">
                    {product?.Price ? getToman(product.Price) || "-" : "-"}
                  </td>
                  <td className="px-6 py-4">
                    {product?.Price
                      ? getToman(product.Price * item.Count) || "-"
                      : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {model.HasPrintWithdrawResource && (
        <div className="mt-6 text-center">
          <button
            onClick={handlePrintInvoice}
            className="px-6 py-3 text-white bg-blue-600 rounded-lg shadow hover:bg-blue-700 disabled:bg-gray-400"
            disabled={isLoading}
          >
            {isLoading ? "در حال چاپ..." : "چاپ فاکتور"}
          </button>
          {errorMessage && (
            <div className="mt-4 text-red-600">{errorMessage}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default Withdraws;
